import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { DailyChallengeService } from "../../daily_challenge/daily_challenge_service.js";
import { formatShortDuration } from "../../common/common_funcs.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";

export class DailyChallengeCompletedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.DAILY_CHALLENGE_COMPLETED_DIALOG;

  private static readonly DIALOG_W = 850;
  private static readonly DIALOG_H = 920;

  private static readonly ICON_SIZE = 240;
  private static readonly CLOSE_SIZE = 100;

  private static readonly TITLE_Y = 150;
  private static readonly TITLE_H = 100;

  private static readonly TEXT_Y = 300;
  private static readonly TEXT_H = 230;

  private static readonly TIMER_Y = 535;
  private static readonly TIMER_W = 306;
  private static readonly TIMER_H = 96;

  private static readonly BTN_W = 780;
  private static readonly BTN_H = 274;
  private static readonly BTN_BOTTOM_PAD = 20;

  private bg!: UINineSliceElement;

  private timer_text!: UITextElement;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( DailyChallengeCompletedDialog.DIALOG_W );
    const body_h = LayoutScaler.heightFromDesign( DailyChallengeCompletedDialog.DIALOG_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createIcon();
    this.createTitle();
    this.createText();
    this.createTimer();
    this.createButton();
    this.createCloseIcon();
  }

  private createBackground(): void
  {
    this.bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( this.bg );

    const icon_bg_size = LayoutScaler.widthFromDesign( 290 );
    const icon_bg_x = this.rect.getW() / 2 - icon_bg_size / 2;
    const icon_bg_y = -icon_bg_size / 2 + LayoutScaler.yFromDesign( 22 );

    const bg_circle = new UIImage(
      this.ctx,
      icon_bg_x,
      icon_bg_y,
      icon_bg_size,
      icon_bg_size,
      ImgID.AvatarCircle,
      {},
      false
    );
    this.addChild( bg_circle );
  }

  private createIcon(): void
  {
    const size = LayoutScaler.widthFromDesign( DailyChallengeCompletedDialog.ICON_SIZE );
    const x = ( this.rect.getW() - size ) / 2;
    const y = -LayoutScaler.yFromDesign( 95 );

    const icon = new UIImage( this.ctx, x, y, size, size, ImgID.IconDailyChallenge );
    this.addChild( icon );
  }

  private createTitle(): void
  {
    const y = LayoutScaler.yFromDesign( DailyChallengeCompletedDialog.TITLE_Y );
    const h = LayoutScaler.heightFromDesign( DailyChallengeCompletedDialog.TITLE_H );
    const w = this.bg.getRect().getW();

    const title_3 = new UITextElement( this.ctx, 0, y + LayoutScaler.yFromDesign( 6 ), w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 32 ),
      outline_color: ColorScheme.daily_challenge_title_outline_3,
    } );

    const title_outline = new UITextElement( this.ctx, 0, y + LayoutScaler.yFromDesign( 3 ), w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 22 ),
      outline_color: ColorScheme.daily_challenge_title_outline,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent
    } );

    const title_outline_2 = new UITextElement( this.ctx, 0, y + LayoutScaler.yFromDesign( 3 ), w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_color: ColorScheme.daily_challenge_title_outline_2,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
    } );

    const title = new UITextElement( this.ctx, 0, y, w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
    } );

    this.bg.addChild( title_3 );
    this.bg.addChild( title_outline );
    this.bg.addChild( title_outline_2 );
    this.bg.addChild( title );
  }

  private createText(): void
  {
    const y = LayoutScaler.yFromDesign( DailyChallengeCompletedDialog.TEXT_Y );
    const h = LayoutScaler.heightFromDesign( DailyChallengeCompletedDialog.TEXT_H );
    const x = this.bg.getRect().getW() * 0.06;
    const w = this.bg.getRect().getW() * 0.88;

    const text = new UITextElement( this.ctx, x, y, w, h, {
      localization_key: "DAILY_CHALLENGE.COMPLETED_TEXT",
      font_size: LayoutScaler.textSizeFromDesign( 50 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Top,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 700,
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_width: LayoutScaler.widthFromDesign( 12 ),
    } );

    const text_1 = new UITextElement( this.ctx, x, y, w, h, {
      localization_key: "DAILY_CHALLENGE.COMPLETED_TEXT",
      font_size: LayoutScaler.textSizeFromDesign( 50 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Top,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 700,
    } );

    this.bg.addChild( text );
    this.bg.addChild( text_1 );
  }

  private createTimer(): void
  {
    const w = LayoutScaler.widthFromDesign( DailyChallengeCompletedDialog.TIMER_W );
    const h = LayoutScaler.heightFromDesign( DailyChallengeCompletedDialog.TIMER_H );
    const x = this.bg.getRect().getW() / 2 - w / 2;
    const y = LayoutScaler.yFromDesign( DailyChallengeCompletedDialog.TIMER_Y );

    const timer = new UIImage( this.ctx, x, y, w, h, ImgID.UI_DailyChallengeTimer );
    this.bg.addChild( timer );

    const text_x = LayoutScaler.widthFromDesign( 80 );
    const text_w = w - text_x - LayoutScaler.widthFromDesign( 25 );

    const seconds = DailyChallengeService.getSecondsUntilReset();
    const text = this.timer_text = new UITextElement( this.ctx, text_x, 0, text_w, h, {
      text: seconds === null ? "" : formatShortDuration( seconds * 1000 ),
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
    } );

    timer.addChild( text );
  }

  private createButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( DailyChallengeCompletedDialog.BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( DailyChallengeCompletedDialog.BTN_H );
    const btn_x = this.bg.getRect().getW() / 2 - btn_w / 2 - LayoutScaler.xFromDesign( 8 );
    const btn_y = this.bg.getRect().getH() - btn_h - LayoutScaler.yFromDesign( DailyChallengeCompletedDialog.BTN_BOTTOM_PAD );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.close();
    } );

    const text = new UITextElement( this.ctx, 0, 0, btn_w, btn_h, {
      localization_key: "OK",
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );
    btn.addChild( text, true );

    this.bg.addChild( btn );
  }

  private createCloseIcon(): void
  {
    const size = LayoutScaler.widthFromDesign( DailyChallengeCompletedDialog.CLOSE_SIZE );
    const x = this.rect.getW() - size + LayoutScaler.xFromDesign( 20 );
    const y = -LayoutScaler.yFromDesign( 80 );

    const icon = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    icon.setOnClick( () => this.close() );

    this.addChild( icon );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public static open(): DailyChallengeCompletedDialog
  {
    return DialogManager.openPooled( "DailyChallengeCompletedDialog", undefined, () => new DailyChallengeCompletedDialog() ) as DailyChallengeCompletedDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse(): void
  {
    const seconds = DailyChallengeService.getSecondsUntilReset();
    this.timer_text.setText( seconds === null ? "" : formatShortDuration( seconds * 1000 ) );
  }

  public name(): string { return "DailyChallengeCompletedDialog"; }
}
